import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsApi, notificationQueryKeys } from "../api/notifications";

const KIND_ICON: Record<string, string> = {
  INFO: "ℹ️",
  SUCCESS: "✅",
  WARNING: "⚠️",
  REMINDER: "🔔",
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "vừa xong";
  if (m < 60) return `${m} phút trước`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} giờ trước`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} ngày trước`;
  return new Date(iso).toLocaleDateString("vi-VN");
}

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const unread = useQuery({
    queryKey: notificationQueryKeys.unread(),
    queryFn: () => notificationsApi.unreadCount(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const list = useQuery({
    queryKey: notificationQueryKeys.list(),
    queryFn: () => notificationsApi.list(20),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationQueryKeys.unread() });
      qc.invalidateQueries({ queryKey: notificationQueryKeys.list() });
    },
  });
  const markAll = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationQueryKeys.unread() });
      qc.invalidateQueries({ queryKey: notificationQueryKeys.list() });
    },
  });

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const count = unread.data ?? 0;
  const items = list.data ?? [];

  function openItem(id: string, link: string | null, isRead: boolean) {
    if (!isRead) markRead.mutate(id);
    setOpen(false);
    if (link) navigate(link);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`Thông báo${count ? ` (${count} chưa đọc)` : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">🔔</span>
        {count > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-80 max-w-[calc(100vw-1rem)] overflow-hidden rounded-lg border bg-popover shadow-lg"
        >
          <div className="flex items-center justify-between border-b px-4 py-2.5">
            <strong className="text-sm font-semibold text-foreground">Thông báo</strong>
            {count > 0 && (
              <button
                type="button"
                className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
                disabled={markAll.isPending}
                onClick={() => markAll.mutate()}
              >
                Đánh dấu tất cả đã đọc
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {list.isLoading ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">Đang tải…</div>
            ) : items.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                Chưa có thông báo nào.
              </div>
            ) : (
              items.map((n) => {
                const isRead = !!n.readAt;
                return (
                  <button
                    key={n.id}
                    type="button"
                    className={`flex w-full items-start gap-3 border-b px-4 py-2.5 text-left transition-colors last:border-b-0 hover:bg-muted ${
                      isRead ? "" : "bg-primary/5"
                    }`}
                    onClick={() => openItem(n.id, n.link, isRead)}
                  >
                    <span className="text-lg leading-none" aria-hidden="true">
                      {KIND_ICON[n.kind] ?? "🔔"}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-sm font-medium text-foreground">{n.title}</span>
                      {n.body && (
                        <span className="text-xs text-muted-foreground">{n.body}</span>
                      )}
                      <span className="mt-0.5 text-[11px] text-muted-foreground">
                        {relativeTime(n.createdAt)}
                      </span>
                    </span>
                    {!isRead && (
                      <span
                        className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
